import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaRing,
  FaChurch,
  FaGlassCheers,
  FaHeart,
  FaUsers,
  FaCalendarCheck,
  FaSnowflake,
  FaSchool,
  FaBuilding,
  FaCheck,
  FaClock,
  FaCar,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import type { IconType } from 'react-icons';

import IconWrapper from './IconWrapper';
import './Services.css';

/* pomocná funkce */
const chunk = <T,>(arr: T[], size: number): T[][] =>
  arr.reduce<T[][]>((acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]), []);

type Service = {
  id: string;
  title: string;
  icon: IconType;
  price: string;
  duration: string;
  description: string;
  features: string[];
  popular?: boolean;
  transport?: string;
  location?: string;
};

const Services: React.FC = () => {
  const [selectedWedding, setSelectedWedding] = useState<string | null>(null);
  const [selectedOther, setSelectedOther] = useState<string | null>(null);
  const [isDesktop, setIsDesktop] = useState<boolean>(window.innerWidth >= 992);

  // sleduj šířku okna a přepínej layout
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const weddings: Service[] = [
    {
      id: 'wedding-small',
      title: 'Malá svatba',
      icon: FaRing,
      price: '7000 Kč',
      duration: '2 hodiny',
      description:
        'Balíček zahrnuje nafocení obřadu, společné skupinové fotografie a portréty novomanželů.',
      features: [
        'Dodání digitálních fotografií do 30 dnů',
        '100 ks zpracovaných fotografií v digitální podobě',
        'On-line galerie',
        'USB disk se všemi upravenými fotografiemi',
      ],
      transport: 'Do 10 km od Frýdku-Místku zdarma, poté 5 Kč/km',
    },
    {
      id: 'wedding-medium',
      title: 'Střední svatba',
      icon: FaChurch,
      price: '14 000 Kč',
      duration: 'cca 6 hodin',
      description:
        'Balíček zahrnuje nafocení obřadu, společné skupinové fotografie, portréty, průběh slavnostního obědu, krájení dortu a první tanec.',
      features: [
        'Dodání digitálních fotografií do 30 dnů',
        '300 ks zpracovaných fotografií digitální podobě',
        '150 ks vytištěných fotografií nebo fotokniha',
        'On-line galerie',
        'USB disk se všemi upravenými fotografiemi',
        '5 ks fotografií v formátu A4',
        '1 ks maxi obraz jedné fotografie jako dárek',
      ],
      transport: 'Do 10 km od Frýdku-Místku zdarma, poté 5 Kč/km',
    },
    {
      id: 'wedding-large',
      title: 'Velká svatba',
      icon: FaGlassCheers,
      price: '18 000 Kč',
      duration: '10–12 hodin',
      description:
        'Balíček zahrnuje nafocení celého svatebního dne od ranních příprav nevěsty až do večera.',
      features: [
        'Dodání digitálních fotografií do 30 dnů',
        '400–500 ks fotografií v digitální podobě',
        '150 ks vytištěných fotografií nebo fotokniha',
        'On-line galerie',
        'USB disk se všemi upravenými fotografiemi',
        '10 ks fotografií ve formátu A4',
        '1 ks maxi obraz jedné fotografie jako dárek',
        'Předsvatební focení zdarma',
      ],
      transport: 'Do 30 km od Frýdku-Místku zdarma, poté 5 Kč/km',
      popular: true,
    },
  ];

  const otherServices: Service[] = [
    {
      id: 'portrait-basic',
      title: 'Předsvatební focení',
      icon: FaHeart,
      price: '3000 Kč',
      duration: '1 hodina',
      description: 'Portrétní focení pro snoubence.',
      features: ['30 ks fotek v digitální podobě', 'Základní retuš', 'Online galerie'],
    },
    {
      id: 'portrait-premium',
      title: 'Rodinné foto',
      icon: FaUsers,
      price: '3000 Kč',
      duration: '1 hodina',
      description: 'Portrétní focení v přírodě nebo v interiéru.',
      features: ['50 ks fotek v digitální podobě', 'Základní retuš', 'Online galerie'],
      transport: 'V rámci Frýdku-Místku zdarma, mimo 6 Kč/km',
      location: 'Focení může probíhat u vás doma nebo na zahradě',
    },
    {
      id: 'branding-product',
      title: 'Firemní focení',
      icon: FaBuilding,
      price: 'od 3000 Kč',
      duration: 'dle potřeby',
      description: 'Prezentační fotografie vaší firmy.',
      features: ['Cenová nabídka na míru'],
      transport: 'Individuálně dle domluvy',
      location: 'Frýdek-Místek a okolí',
    },
    {
      id: 'family-outdoor',
      title: 'Vánoční focení',
      icon: FaSnowflake,
      price: '2500 Kč',
      duration: '30 minut',
      description: 'Focení v interiéru nebo exteriéru podle aktuální nabídky.',
      features: ['10 ks fotek v digitální podobě', 'Základní retuš', 'Online galerie'],
      location: 'Frýdek-Místek',
    },
    {
      id: 'maternity-newborn',
      title: 'Školní foto',
      icon: FaSchool,
      price: 'od 60 Kč/ks',
      duration: 'dle potřeby',
      description:
        'Focení tříd škol a školek, individuální fotky dětí a skupinek.',
      features: ['60 ks fotek v digitální podobě', 'Jemná retuš', 'Možnost zapůjčení rekvizit'],
    },
    {
      id: 'event-coverage',
      title: 'Focení událostí',
      icon: FaCalendarCheck,
      price: 'od 3000 Kč',
      duration: 'dle potřeby',
      description:
        'Reportážní focení společenských akcí, oslav, křtin nebo firemních událostí.',
      features: ['Focení dle dohody', 'Spontánní momenty', 'Online galerie'],
    },
  ];

  /** Render jedné karty */
  const ServiceCard: React.FC<{
    service: Service;
    onOpen: () => void;
    isOpen: boolean;
  }> = ({ service, onOpen }) => (
    <Card className={`service-detail-card ${service.popular ? 'popular' : ''}`}>
      {service.popular && (
        <Badge bg="primary" className="popular-badge">Nejoblíbenější</Badge>
      )}
      <Card.Body>
        <div className="service-icon-large">
          <IconWrapper icon={service.icon} />
        </div>
        <Card.Title>{service.title}</Card.Title>
        <div className="service-price-large">{service.price}</div>
        <div className="service-duration">
          <IconWrapper icon={FaClock} className="me-2" />
          {service.duration}
        </div>
        <Card.Text>{service.description}</Card.Text>
        <Button variant="outline-primary" onClick={onOpen} className="w-100">
          Více informací
        </Button>
      </Card.Body>
    </Card>
  );

  /** Render detail panelu */
  const Details: React.FC<{ s: Service; onClose: () => void }> = ({ s, onClose }) => (
    <Card className="details-card">
      <Card.Body>
        {/* speciální text pro školní/firemní */}
        {['maternity-newborn', 'branding-product'].includes(s.id) ? (
          <div className="text-center">
            <h3 className="mb-3">{s.title}</h3>
            <p className="lead mb-4">
              {s.id === 'maternity-newborn'
                ? 'V případě zájmu o focení ve vaší škole/školce mě neváhejte kontaktovat, vytvořím Vám nabídku na míru pro vás.'
                : 'Máte zájem o firemní focení? Napište mi pár detailů (typ focení, počet lidí, místo a termín) a připravím nabídku na míru.'}
            </p>
            <Link to="/contact" className="btn btn-primary">Kontaktovat</Link>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <h3 className="flex-grow-1 text-center">{s.title}</h3>
              <Button variant="outline-secondary" size="sm" onClick={onClose}>
                Zavřít
              </Button>
            </div>
            <Row>
              <Col md={6}>
                <h5>Balíček obsahuje:</h5>
                <ul className="features-list">
                  {s.features.map((f, i) => (
                    <li key={i}>
                      <IconWrapper icon={FaCheck} className="me-2 text-success" />
                      {f}
                    </li>
                  ))}
                </ul>
              </Col>
              <Col md={6}>
                <h5>Další informace:</h5>
                <div className="info-item">
                  <IconWrapper icon={FaClock} className="me-2" />
                  <strong>Doba trvání:</strong> {s.duration}
                </div>
                {s.transport && (
                  <div className="info-item">
                    <IconWrapper icon={FaCar} className="me-2" />
                    <strong>Doprava:</strong> {s.transport}
                  </div>
                )}
                {s.location && (
                  <div className="info-item">
                    <IconWrapper icon={FaMapMarkerAlt} className="me-2" />
                    <strong>Místo:</strong> {s.location}
                  </div>
                )}
              </Col>
            </Row>
          </>
        )}
      </Card.Body>
    </Card>
  );

  return (
    <div className="services-page">
      <div className="services-hero">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="services-title">Nabídka služeb</h1>
            <p className="services-subtitle">Vše, co pro vás mohu nafotit.</p>
          </motion.div>
        </Container>
      </div>

      <Container className="py-5">
        <h1 className="text-center mb-5">Svatební fotografie</h1>

        {/* === WEDDINGS === */}
        {isDesktop ? (
          /* Desktop: po trojicích */
          chunk(weddings, 3).map((group, rowIdx) => (
            <React.Fragment key={`wed-row-${rowIdx}`}>
              <Row className="g-4 mb-4">
                {group.map((s, index) => (
                  <Col lg={4} md={6} key={s.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 50 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.15 }}
                      viewport={{ once: true }}
                    >
                      <ServiceCard
                        service={s}
                        isOpen={selectedWedding === s.id}
                        onOpen={() =>
                          setSelectedWedding(prev => (prev === s.id ? null : s.id))
                        }
                      />
                    </motion.div>
                  </Col>
                ))}
              </Row>
              {selectedWedding && group.some(s => s.id === selectedWedding) && (
                <Row className="mb-5">
                  <Col xs={12}>
                    <Details
                      s={group.find(s => s.id === selectedWedding)!}
                      onClose={() => setSelectedWedding(null)}
                    />
                  </Col>
                </Row>
              )}
            </React.Fragment>
          ))
        ) : (
          /* Mobile/Tablet: plochý seznam; detail hned pod kartou */
          <Row className="g-4 mb-4">
            {weddings.map((s, index) => (
              <React.Fragment key={s.id}>
                <Col lg={4} md={6}>
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.15 }}
                    viewport={{ once: true }}
                  >
                    <ServiceCard
                      service={s}
                      isOpen={selectedWedding === s.id}
                      onOpen={() =>
                        setSelectedWedding(prev => (prev === s.id ? null : s.id))
                      }
                    />
                  </motion.div>
                </Col>
                {selectedWedding === s.id && (
                  <Col xs={12}>
                    <Details s={s} onClose={() => setSelectedWedding(null)} />
                  </Col>
                )}
              </React.Fragment>
            ))}
          </Row>
        )}

        {/* === OTHERS === */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="pricing-section"
        >
          <h1 className="text-center mb-5">Další služby</h1>

          {isDesktop
            ? chunk(otherServices, 3).map((group, rowIdx) => (
              <React.Fragment key={`other-row-${rowIdx}`}>
                <Row className="g-4 mb-4">
                  {group.map((s, index) => (
                    <Col lg={4} md={6} key={s.id}>
                      <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.15 }}
                        viewport={{ once: true }}
                      >
                        <ServiceCard
                          service={s}
                          isOpen={selectedOther === s.id}
                          onOpen={() =>
                            setSelectedOther(prev => (prev === s.id ? null : s.id))
                          }
                        />
                      </motion.div>
                    </Col>
                  ))}
                </Row>
                {selectedOther && group.some(s => s.id === selectedOther) && (
                  <Row className="mb-5">
                    <Col xs={12}>
                      <Details
                        s={group.find(s => s.id === selectedOther)!}
                        onClose={() => setSelectedOther(null)}
                      />
                    </Col>
                  </Row>
                )}
              </React.Fragment>
            ))
            : (
              <Row className="g-4 mb-4">
                {otherServices.map((s, index) => (
                  <React.Fragment key={s.id}>
                    <Col lg={4} md={6}>
                      <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.15 }}
                        viewport={{ once: true }}
                      >
                        <ServiceCard
                          service={s}
                          isOpen={selectedOther === s.id}
                          onOpen={() =>
                            setSelectedOther(prev => (prev === s.id ? null : s.id))
                          }
                        />
                      </motion.div>
                    </Col>
                    {selectedOther === s.id && (
                      <Col xs={12}>
                        <Details s={s} onClose={() => setSelectedOther(null)} />
                      </Col>
                    )}
                  </React.Fragment>
                ))}
              </Row>
            )}
        </motion.div>
      </Container>
    </div>
  );
};

export default Services;